'use client'

import clsx from 'clsx'
import { ReactNode, useEffect, useState } from 'react'
import Button from '../Button/Button'
import Header from '../Header/Header'
import TwoThreeFourItem, { TwoThreeFourItemProps } from './TwoThreeFourItem'

import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import 'swiper/css/a11y'

import { BlogFeedItem, useBlogFeedApi } from '@/api/useBlogFeedApi'
import { Navigation } from 'swiper/modules'
import Icon from '../Icon/Icon'

/**
 * Strip HTML tags and decode HTML entities from a string
 */
function stripHtmlAndDecode(html: string | undefined): string {
  if (!html) return ''

  // Create a temporary DOM element to parse HTML
  const temp = document.createElement('div')
  temp.innerHTML = html

  // Get text content (automatically strips tags and decodes entities)
  let text = temp.textContent || temp.innerText || ''

  // Clean up extra whitespace
  text = text.replace(/\s+/g, ' ').trim()

  // Truncate to a reasonable length (200 characters)
  if (text.length > 200) {
    text = text.substring(0, 197) + '...'
  }

  return text
}

/**
 * Transform blog feed items to TwoThreeFourItemProps format
 */
function transformBlogFeedToCards(
  blogItems: BlogFeedItem[],
  buttonText: string = 'Read More',
): TwoThreeFourItemProps[] {
  return blogItems.map((item) => ({
    header: item.title,
    body: stripHtmlAndDecode(item.summary),
    image:
      item.thumbnail || item.image
        ? {
            src: item.thumbnail || item.image || '',
            alt: item.title,
          }
        : undefined,
    button: {
      text: buttonText,
      link: {
        href: item.url,
      },
    },
  }))
}

export interface TwoThreeFourProps {
  /**
   * Optional copy for header/title
   */
  header?: string | ReactNode
  /**
   * Optional copy for subheader
   */
  subheader?: string | ReactNode
  /**
   * Background color
   */
  backgroundColor: 'white' | 'grey'
  /**
   * Cards layout
   */
  layout: 'twoup' | 'threeup' | 'fourup'
  /**
   * List of cards (optional if using blog feed)
   */
  cards?: TwoThreeFourItemProps[]
  /**
   * Whether to use blog feed data instead of cards prop
   */
  useBlogFeed?: boolean
  /**
   * Button text for blog feed items
   */
  blogButtonText?: string
}

export default function TwoThreeFour({
  header,
  subheader,
  backgroundColor,
  layout,
  cards,
  useBlogFeed = false,
  blogButtonText = 'Read More',
}: TwoThreeFourProps) {
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  // Fetch blog feed data if useBlogFeed is enabled
  const { data: blogFeedData, isLoading, error } = useBlogFeedApi(useBlogFeed)

  // Transform blog feed data to cards format
  const blogCards = blogFeedData
    ? transformBlogFeedToCards(blogFeedData, blogButtonText)
    : []

  // Use blog cards if useBlogFeed is enabled, otherwise use provided cards
  const displayCards = useBlogFeed ? blogCards : cards || []

  const headerBlock = (
    <>
      <Header
        as="h2"
        overrideClassName
        className="type-headline-2 mb-2 text-center px-6 lg:type-display-3 lg:text-left lg:pl-16 lg:mb-4"
      >
        {header}
      </Header>
      {subheader && (
        <p className="type-body-6 mb-8 text-center px-6 lg:text-left lg:pl-16">
          {subheader}
        </p>
      )}
    </>
  )

  const sectionClassName = clsx(
    'py-10 lg:py-12',
    backgroundColor === 'grey' ? 'bg-neutral-200' : 'bg-white',
  )

  if (useBlogFeed && (!hasMounted || isLoading)) {
    return (
      <div className={sectionClassName}>
        {headerBlock}
        <div className="text-center px-6">
          <p className="type-body-6">Loading blog posts...</p>
        </div>
      </div>
    )
  }

  if (useBlogFeed && error) {
    return (
      <div className={sectionClassName}>
        {headerBlock}
        <div className="text-center px-6">
          <p className="type-body-6 text-red-600">
            Failed to load blog posts. Please try again later.
          </p>
        </div>
      </div>
    )
  }

  if (displayCards.length === 0) {
    return (
      <div className={sectionClassName}>
        {headerBlock}
        <div className="text-center px-6">
          <p className="type-body-6">No content available at this time.</p>
        </div>
      </div>
    )
  }
  return (
    <div className={sectionClassName}>
      {headerBlock}
      <Swiper
        navigation={{
          nextEl: '.button-next',
          prevEl: '.button-prev',
        }}
        slidesPerView={1.2}
        spaceBetween={12}
        breakpoints={{
          640: {
            slidesPerView: 2.2,
            spaceBetween: 16,
          },
          768: {
            slidesPerView: 3.2,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 4.2,
            spaceBetween: 20,
          },
        }}
        modules={[Navigation]}
        className="!px-4 md:!px-6 lg:!px-8"
      >
        {displayCards.map((card, index) => (
          <SwiperSlide
            key={index}
            className={clsx(
              // 'max-w-[327px]',
              layout === 'twoup' && 'md:w-[751px]',
              layout === 'threeup' && 'md:w-[490px]',
              layout === 'fourup' && 'md:[&_h3]:text-[18px]',
              // layout === 'fourup' && 'md:[&_h3]:text-[21px] md:max-w-[360px]',
            )}
          >
            <TwoThreeFourItem {...card} />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="flex gap-6 pt-8 px-6 lg:pl-16 lg:pt-10">
        <Button
          variant="secondary"
          overrideClassName
          className="ml-2 button-prev flex border-2 border-theme-primary rounded-full p-2 focus:outline-accent-link focus:outline-offset-4 focus:outline-1 hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-75 disabled:bg-white disabled:text-neutral-600 disabled:border-neutral-600"
          aria-label="Scroll to previous slide"
        >
          <Icon icon="arrow-left" className="w-7 h-7 text-theme-primary"></Icon>
        </Button>
        <Button
          variant="secondary"
          overrideClassName
          className="button-next flex border-2 border-theme-primary rounded-full p-2 focus:outline-accent-link focus:outline-offset-4 focus:outline-1 hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-75 disabled:bg-white disabled:text-neutral-600 disabled:border-neutral-600"
          aria-label="Scroll to next slide"
        >
          <Icon
            icon="arrow-right"
            className="w-7 h-7 text-theme-primary"
          ></Icon>
        </Button>
      </div>
    </div>
  )
}
